<template>
  <validation-observer ref="containerForm" v-slot="{ invalid }">
    <form @submit.prevent="handleSubmit">
      <validation-provider name="Supplier" v-slot="context" rules="required">
        <multi-select
          v-model="supplier"
          placeholder="Pick a supplier"
          track-by="id"
          label="invoice_legal_name"
          :options="suppliers"
          :internal-search="false"
          @search-change="debouncedFunction"
          @input="product = null"
        />

        <small v-if="context.errors.length" v-bind="context.ariaMsg" class="text-danger form-text">
          {{ context.errors[0] }}
        </small>
      </validation-provider>

      <template v-if="supplier">
        <hr />

        <validation-provider name="Product" v-slot="context" rules="required">
          <multi-select
            v-model="product"
            placeholder="Pick a product"
            :options="supplierProducts"
            track-by="id"
            label="name"
            @input="changedProduct"
          />

          <small v-if="context.errors.length" v-bind="context.ariaMsg" class="text-danger form-text">
            {{ context.errors[0] }}
          </small>
        </validation-provider>
      </template>

      <template v-if="product">
        <hr />

        <validation-provider name="Supplier" v-slot="context" rules="required">
          <multi-select
            v-model="selectedVariations"
            placeholder="Pick a variation"
            track-by="id"
            label="variation_name"
            :options="filteredVariations"
            :multiple="true"
            :preserve-search="true"
            :close-on-select="false"
            :clear-on-select="false"
            :custom-label="nameWithLang"
          />

          <small v-if="context.errors.length" v-bind="context.ariaMsg" class="text-danger form-text">
            {{ context.errors[0] }}
          </small>
        </validation-provider>
        <hr />

        <b-form-group label="Container status">
          <b-form-checkbox v-model="status" switch>
            Active
          </b-form-checkbox>
          <small class="text-muted">
            Inactive containers do not issue new codes. Previously issued codes remain available.
          </small>
        </b-form-group>

        <hr />

        <validation-provider name="Valid For" v-slot="context" rules="required">
          <b-form-group label="Valid For (Participants)">
            <b-form-input
              v-model="valid_for"
              placeholder="e.g 1"
              :state="getValidationState(context)"
              v-bind="context.ariaInput"
            />

            <b-form-invalid-feedback v-bind="context.ariaMsg">
              {{ context.errors[0] }}
            </b-form-invalid-feedback>
          </b-form-group>
        </validation-provider>

        <validation-provider name="Templates" rules="required">
          <b-form-group label="Templates">
            <b-tabs>
              <b-tab title="Email">
                <wysiwyg-editor v-model="email_template" />
              </b-tab>
              <b-tab title="PDF" active>
                <wysiwyg-editor v-model="pdf_template" />
              </b-tab>
            </b-tabs>
          </b-form-group>
        </validation-provider>

        <b-row>
          <b-col>
            <validation-provider name="PDF Title" v-slot="context">
              <b-form-group label="PDF Title">
                <b-form-input
                  v-model="pdf_title"
                  placeholder="e.g Lorem ipsum"
                  :state="getValidationState(context)"
                  v-bind="context.ariaInput"
                />

                <b-form-invalid-feedback v-bind="context.ariaMsg">
                  {{ context.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col>
            <validation-provider name="PDF Link Text" v-slot="context">
              <b-form-group label="PDF Link Text">
                <b-form-input
                  v-model="pdf_link_text"
                  placeholder="e.g Dolor Sit Amet"
                  :state="getValidationState(context)"
                  v-bind="context.ariaInput"
                />

                <b-form-invalid-feedback v-bind="context.ariaMsg">
                  {{ context.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>
        
        <div class="mb-2">Batches Settings</div>

        <template v-if="batches">
          <template v-for="(batch, index) in batches">
            <div class="my-3" :key="index">
              <b-row>
                <b-col>
                  <b-input-group class="d-flex">
                    <validation-provider name="Batch" v-slot="context" rules="required" class="flex-grow-1">
                      <multi-select
                        v-model="batches[index].batch"
                        placeholder="Pick a batch"
                        :options="titles"
                        track-by="id"
                        label="title"
                      />

                      <small v-if="context.errors.length" v-bind="context.ariaMsg" class="text-danger form-text">
                        {{ context.errors[0] }}
                      </small>
                    </validation-provider>

                    <template #append>
                      <b-button
                        variant="danger"
                        @click="() => removeBatch(index)"
                      >
                        <strong>&times;</strong>
                      </b-button>
                    </template>
                  </b-input-group>
                </b-col>
              </b-row>

              <b-row class="mt-1">
                <b-col>
                  <b-form-checkbox v-model="batches[index].generate_barcodes" switch>
                    Generate barcodes
                  </b-form-checkbox>
                </b-col>

                <b-col>
                  <b-form-checkbox v-model="batches[index].add_prefix_or_suffix" switch>
                    Add prefix or suffix
                  </b-form-checkbox>
                </b-col>
              </b-row>

              <b-row v-if="batches[index].add_prefix_or_suffix" class="mt-1">
                <b-col>
                  <b-form-group label="Add prefix">
                    <b-form-input
                      placeholder="e.g -M"
                      v-model="batches[index].code_prefix"
                    />
                  </b-form-group>
                </b-col>

                <b-col>
                  <b-form-group label="Add suffix">
                    <b-form-input
                      placeholder="e.g -M"
                      v-model="batches[index].code_suffix"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </div>
          </template>
        </template>

        <b-button variant="light" size="sm" class="mt-2" @click="addEmptyBatch">
          Add Batch
        </b-button>

        <b-row align-h="end">
          <b-col cols="auto">
            <b-button type="submit" variant="primary" :disabled="$cannot('supplier.manage') || invalid || submitting">
              {{ submitting ? 'Adding...' : 'Add' }}
            </b-button>
          </b-col>
        </b-row>
      </template>
    </form>
  </validation-observer>
</template>

<script>
import 'vue-multiselect/dist/vue-multiselect.min.css'
import MultiSelect from 'vue-multiselect'
import {getValidationState} from '../page-helpers'
import {containers, products, suppliers} from '@/api'
import WysiwygEditor from '@/components/WysiwygEditor';
import {debounce} from "ts-debounce";
import {
  activateWithConflicts,
  buildContainerPayload,
  conflictMessage,
  containerErrorMessage,
  findOverlappingActiveContainers,
  loadAllContainers,
} from './csv-container-status'

export default {
  name: 'CSVCodesBatchForm',
  inject: ['$cannot'],
  components: { WysiwygEditor, MultiSelect },

  beforeMount() {
    this.findSupplier('')
  },

  data() {
    return {
      selectedVariations: null,
      variations: [],
      suppliers: [],
      titles: [],
      batches: [],
      product: null,
      supplier: null,

      valid_for: 1,
      email_template: '',
      pdf_template: '',
      pdf_title: '',
      pdf_link_text: '',
      status: true,
      submitting: false,

      debouncedFunction: debounce(this.findSupplier, 300)
    }
  },

  computed: {
    supplierProducts() {
      const uniqueProducts = [];

      if (this.supplier) {
        this.supplier.variations.map((variation) => {
          if (uniqueProducts.findIndex((product) => product.id === variation.product_id) === -1) {
            uniqueProducts.push(variation.product)
          }
        })
      }

      return uniqueProducts;
    },

    filteredVariations() {
      if (!this.supplier) {
        return [];
      }

      return this.supplier.variations.filter((variation) => variation.product_id === this.product.id);
    }
  },

  watch: {
    filteredVariations(newVariations) {
      if (newVariations.length === 1) {
        this.email_template = newVariations[0].email_template;
      }
    }
  },

  methods: {
    getValidationState,

    addEmptyBatch() {
      this.batches.push({
        generate_barcodes: false,
        add_prefix_or_suffix: false,
        code_prefix: '',
        code_suffix: '',
      })
    },

    removeBatch(index) {
      if (this.batches.length === 1) {
        this.batches = []
        return
      }

      this.batches.splice(index, 1)
    },

    async findSupplier(text) {
      await suppliers.all({filter: {search: text}})
        .then((res) => {
          this.suppliers = res.data()
          this.$forceUpdate();
        })
      await products.variations.batches
        .titles()
        .then((res) => {
          this.titles = res.data
          this.$forceUpdate();
        })
    },

    nameWithLang({ variation_name, place }) {
      return place !== null ?
        `${variation_name}, ${place.city.name}` :
        `${variation_name}`
    },

    containerPayload(status = this.status) {
      return {
        ...buildContainerPayload({
          status,
          product_id: this.product.id,
          product_variations: this.selectedVariations,
          valid_for: this.valid_for,
          email_template: this.email_template,
          pdf_template: this.pdf_template,
          pdf_title: this.pdf_title,
          pdf_link_text: this.pdf_link_text,
          batches: this.batches,
        }, status),
        supplier_id: this.supplier.id,
      }
    },

    async confirmReplacement(conflicts) {
      if (!conflicts.length) {
        return true
      }

      return this.$bvModal.msgBoxConfirm(conflictMessage(conflicts, this.selectedVariations), {
        title: 'Replace active containers?',
        okTitle: 'Create and replace',
        okVariant: 'warning',
        cancelTitle: 'Cancel',
      })
    },

    async handleSubmit() {
      this.submitting = true
      let createdId = null

      try {
        await this.$store.dispatch('auth/refreshToken')
        const payload = this.containerPayload()
        let conflicts = []

        if (this.status) {
          const allContainers = await loadAllContainers(containers)
          conflicts = findOverlappingActiveContainers(allContainers, this.selectedVariations)

          if (!await this.confirmReplacement(conflicts)) {
            return
          }
        }

        if (conflicts.length) {
          const response = await containers.create({ ...payload, status: false })
          createdId = response.data.id
          const updatePayload = { ...payload }
          delete updatePayload.supplier_id
          await activateWithConflicts(
            containers,
            { ...payload, id: createdId, product_variations: this.selectedVariations },
            conflicts,
            updatePayload,
          )
        } else {
          await containers.create(payload)
        }

        this.$bvToast.toast(`CSV codes container created.`, {
          title: 'CSV codes container created',
          variant: 'primary',
        })

        this.$emit('close')
      } catch (error) {
        if (error.errors && this.$refs.containerForm) {
          this.$refs.containerForm.setErrors(error.errors)
        }

        const deactivatedIds = error.containerStatusProgress?.deactivatedIds ?? []
        const partial = createdId
          ? ` Container #${createdId} was created inactive.${deactivatedIds.length ? ` Containers ${deactivatedIds.map((id) => `#${id}`).join(', ')} were deactivated.` : ''}`
          : ''

        this.$bvToast.toast(`${containerErrorMessage(error)}${partial}`, {
          title: createdId ? 'Container replacement incomplete' : 'Container creation failed',
          variant: 'danger',
        })

        if (createdId) {
          this.$emit('close')
        }
      } finally {
        this.submitting = false
      }
    },

    changedProduct() {
      this.selectedVariations = [];
      this.email_template = '';
      this.pdf_template = '';
      this.pdf_title = '';
      this.pdf_link_text = '';
      this.batches = [];

      if (this.filteredVariations.length === 1) {
        this.selectedVariations.push(this.filteredVariations[0]);
      }
    },
  },
}
</script>
